import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SLV_CLOSE_SEARCHABLE_SELECT_EVENT } from "../../utils/searchableSelectEvents";

// ── Design tokens ─────────────────────────────────────────────
export const T = {
  bg:       "var(--c-bg)",
  s1:       "var(--c-s1)",
  s2:       "var(--c-s2)",
  s3:       "var(--c-s3)",
  border:   "var(--c-border)",
  border2:  "var(--c-border2)",
  acc:      "var(--c-acc)",
  acc2:     "var(--c-acc2)",
  acc3:     "var(--c-acc3)",
  acc4:     "var(--c-acc4)",
  sos:      "var(--c-sos)",
  text:     "var(--c-text)",
  muted:    "var(--c-muted)",
  muted2:   "var(--c-muted2)",
  fontMono: "'DM Mono', monospace",
  fontSans: "'Outfit', sans-serif",
};

// ── Btn ───────────────────────────────────────────────────────
export function Btn({ children, variant="primary", size="md", onClick, disabled, style={}, icon, full }) {
  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    gap: 6, border: "none", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: T.fontSans, fontWeight: 600, transition: "all 0.18s",
    opacity: disabled ? 0.5 : 1, width: full ? "100%" : "auto",
    whiteSpace: "nowrap",
  };
  const sizes = {
    sm: { padding: "5px 12px", fontSize: 12 },
    md: { padding: "8px 18px", fontSize: 13 },
    lg: { padding: "11px 24px", fontSize: 14 },
  };
  const variants = {
    primary: { background: T.acc, color: "#fff" },
    danger:  { background: T.sos, color: "#fff" },
    success: { background: T.acc3, color: T.bg },
    ghost:   { background: "rgba(255,255,255,0.04)", color: T.text, border: `1px solid ${T.border2}` },
    outline: { background: "transparent", color: T.acc, border: `1px solid ${T.acc}` },
  };
  return (
    <button style={{ ...base, ...sizes[size], ...variants[variant], ...style }} onClick={onClick} disabled={disabled}>
      {icon && <span>{icon}</span>}{children}
    </button>
  );
}

// ── Badge ─────────────────────────────────────────────────────
export function Badge({ children, color, bg, border }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", padding:"2px 8px", borderRadius:20, fontSize:10, fontFamily:T.fontMono, letterSpacing:"0.5px", fontWeight:500, color: color||T.acc, background: bg||"rgba(124,106,247,0.1)", border: `1px solid ${border||"rgba(124,106,247,0.3)"}` }}>
      {children}
    </span>
  );
}

// ── Card ──────────────────────────────────────────────────────
export function Card({ children, style={}, onClick }) {
  return (
    <div style={{ background:T.s2, border:`1px solid ${T.border}`, borderRadius:10, padding:14, ...style }} onClick={onClick}>
      {children}
    </div>
  );
}

// ── Stat box ──────────────────────────────────────────────────
export function StatBox({ label, value, color, sub }) {
  return (
    <div style={{ background:T.s2, border:`1px solid ${T.border}`, borderRadius:8, padding:"10px 14px", minWidth:80 }}>
      <div style={{ fontFamily:T.fontMono, fontSize:22, fontWeight:700, color: color||T.text, lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:11, color:T.muted, marginTop:4 }}>{label}</div>
      {sub && <div style={{ fontSize:10, color:T.muted2, marginTop:2, fontFamily:T.fontMono }}>{sub}</div>}
    </div>
  );
}

// ── Section label ─────────────────────────────────────────────
export function SectionLabel({ children, action }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
      <span style={{ fontFamily:T.fontMono, fontSize:10, letterSpacing:"2px", color:T.muted, textTransform:"uppercase" }}>{children}</span>
      {action}
    </div>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────
const MODAL_Z = 20000;

export function Modal({ open, onClose, title, children, width=480 }) {
  const titleId = React.useId();
  if (!open) return null;
  if (typeof document === "undefined") return null;
  return createPortal(
    <div      className="modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,5,10,0.8)",
        backdropFilter: "blur(6px)",
        zIndex: MODAL_Z,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        boxSizing: "border-box",
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="modal-content"
        style={{
          background: T.s1,
          border: `1px solid ${T.border2}`,
          borderRadius: 14,
          width,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:`1px solid ${T.border}` }}>
          <span id={titleId} style={{ fontFamily:T.fontSans, fontSize:16, fontWeight:700, color:T.text }}>{title}</span>
          <button type="button" onClick={onClose} style={{ background:"none", border:"none", color:T.muted, fontSize:20, cursor:"pointer", lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:"20px", overflowY:"auto", flex:1 }}>{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ── FormField ─────────────────────────────────────────────────
export function Field({ label, children, error }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:"block", fontSize:12, color:T.muted, marginBottom:5, fontFamily:T.fontMono, letterSpacing:"0.5px" }}>{label}</label>
      {children}
      {error && <div style={{ color:T.sos, fontSize:11, marginTop:4 }}>{error}</div>}
    </div>
  );
}

export function Input({ value, onChange, placeholder, type="text", style={} }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{ width:"100%", background:T.s2, border:`1px solid ${T.border2}`, borderRadius:8, padding:"9px 12px", color:T.text, fontFamily:T.fontSans, fontSize:13, outline:"none", boxSizing:"border-box", ...style }}
    />
  );
}

export function Select({ value, onChange, children, style={} }) {
  return (
    <select value={value} onChange={onChange} style={{ width:"100%", background:T.s2, border:`1px solid ${T.border2}`, borderRadius:8, padding:"9px 12px", color:T.text, fontFamily:T.fontSans, fontSize:13, outline:"none", boxSizing:"border-box", ...style }}>
      {children}
    </select>
  );
}

// ── Searchable select ───────────────────────────────────────────
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  noResultsLabel = "No options",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query) return options || [];
    const q = query.toLowerCase();
    return (options || []).filter(o =>
      String(o.label || "").toLowerCase().includes(q) ||
      String(o.value || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  const selected = (options || []).find(o => String(o.value) === String(value));

  const handleSelect = (val) => {
    onChange && onChange(val);
    setOpen(false);
  };

  useEffect(() => {
    const onGlobalClose = () => setOpen(false);
    window.addEventListener(SLV_CLOSE_SEARCHABLE_SELECT_EVENT, onGlobalClose);
    return () => window.removeEventListener(SLV_CLOSE_SEARCHABLE_SELECT_EVENT, onGlobalClose);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handleOutside, true);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("pointerdown", handleOutside, true);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <div ref={rootRef} style={{ position:"relative", zIndex: 50 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          background:T.s2,
          border:`1px solid ${T.border2}`,
          borderRadius:8,
          padding:"9px 12px",
          color: selected ? T.text : T.muted2,
          fontFamily:T.fontSans,
          fontSize:13,
          cursor:"pointer",
          userSelect:"none",
        }}
      >
        {selected ? selected.label : placeholder}
      </div>
      {open && (
        <div
          style={{
            position:"absolute",
            zIndex:1200,
            marginTop:4,
            background:T.s2,
            border:`1px solid ${T.border2}`,
            borderRadius:8,
            boxShadow:"0 10px 40px rgba(0,0,0,0.4)",
            width:"100%",
            maxHeight:260,
            overflow:"hidden",
          }}
        >
          <div style={{ padding:8, borderBottom:`1px solid ${T.border2}` }}>
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Type to search…"
            />
          </div>
          <div style={{ maxHeight:210, overflowY:"auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding:"8px 12px", fontSize:12, color:T.muted }}>{noResultsLabel}</div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  style={{
                    padding:"8px 12px",
                    fontSize:13,
                    fontFamily:T.fontSans,
                    color:String(opt.value) === String(value) ? T.acc : T.text,
                    background:String(opt.value) === String(value) ? "rgba(124,106,247,0.08)" : "transparent",
                    cursor:"pointer",
                  }}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function Textarea({ value, onChange, placeholder, rows=3 }) {
  return (
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      style={{ width:"100%", background:T.s2, border:`1px solid ${T.border2}`, borderRadius:8, padding:"9px 12px", color:T.text, fontFamily:T.fontSans, fontSize:13, outline:"none", resize:"vertical", boxSizing:"border-box" }} />
  );
}

// ── Notification toast ────────────────────────────────────────
export function Toast({ notification }) {
  if (!notification) return null;
  const colors = { success:"#6af7c4", error:"#ff4466", info:"#7c6af7", warning:"#f7c46a" };
  return (
    <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", background:T.s1, border:`1px solid ${colors[notification.type]||T.border2}`, borderRadius:10, padding:"10px 20px", zIndex:9999, fontFamily:T.fontMono, fontSize:12, color:colors[notification.type]||T.text, backdropFilter:"blur(10px)", boxShadow:"0 8px 32px rgba(0,0,0,0.5)", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:8 }}>
      <span>{{ success:"✓", error:"✕", info:"ℹ", warning:"⚠" }[notification.type]}</span>
      {notification.message}
    </div>
  );
}
